/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  Form,
  Divider,
  Dropzone,
  Image,
  Icon,
  Dimmer,
  Loader,
  PhoneInput,
  Tooltip,
} from '@mairistem/composants';

import { optionsCivilites } from '@/constants';
import type { ParametresGeneraux } from '@/types';
import { toBase64 } from '@/utils';

import { toast } from 'react-toastify';
import TabParametre from '../TabParametre';
import TabCollectiviteAdresse from './TabCollectiviteAdresse';

export interface TabCollectiviteProps {
  parametres: ParametresGeneraux;
  loadingImages: boolean;
  errors?: any;
  onChange: (
    e: React.SyntheticEvent,
    data: { name: string; value: string },
  ) => void;
}

const TabCollectivite = (props: TabCollectiviteProps) => {
  const { parametres, loadingImages, errors = {}, onChange } = props;

  const {
    nomMaire,
    identifiantCiviliteMaire,
    telephone1,
    telephone2,
    adresse,
    adresseEmail,
    libelleArrondissement,
    codeArrondissement,
    imageCachet,
    // cheminCachet,
    imageSignature,
    // cheminSignature,
  } = parametres || {};

  const handleResetImage = React.useCallback(
    (name) => {
      onChange(null, {
        name,
        value: null,
      });
    },
    [onChange],
  );

  const handleChangeDrop = React.useCallback(
    (files, imageName) => {
      toBase64(files[0]).then((base64: string) => {
        const MAX_FILE_SIZE = 3 * 1024 * 1024;

        const base64Length = base64.length;
        const padding = base64.endsWith('==')
          ? 2
          : base64.endsWith('=')
            ? 1
            : 0;
        const fileSizeInBytes = (base64Length * 3) / 4 - padding;

        if (fileSizeInBytes > MAX_FILE_SIZE) {
          toast('Le fichier dépasse la limite de 3 Mo', { type: 'error' });
        } else {
          onChange(null, {
            name: imageName,
            value: base64, // Conversion en base64
          });
        }
      });
    },
    [onChange],
  );

  const handleEmailChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      const invalidPatterns = /@.*@|\.\.|\s/;

      if (invalidPatterns.test(value)) {
        return;
      }

      onChange(e, { name, value });
    },
    [onChange],
  );

  const handleChangeTelephone = React.useCallback(
    (value, name) => {
      onChange(null, {
        name,
        value,
      });
    },
    [onChange],
  );

  const renderImage = (image, name) =>
    _.isEmpty(image) || _.isNil(image) ? (
      <Dropzone
        style={{ cursor: 'pointer' }}
        // @ts-expect-error 2322
        onDrop={(e) => handleChangeDrop(e, name)}
      />
    ) : (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4rem',
        }}
      >
        <Image
          inline
          bordered
          src={image}
          style={{
            width: 100,
            height: 100,
            objectFit: 'contain',
          }}
          alt="cachet"
        />
        <Icon
          data-testid="reset-image-cachet"
          name="close"
          link
          color="red"
          onClick={() => handleResetImage(name)}
        />
      </div>
    );

  return (
    <TabParametre title="INFORMATIONS DE LA MAIRIE">
      <Form.Group>
        <Tooltip
          trigger={
            <Form.Dropdown
              width={4}
              fluid
              selection
              data-testid="parametre-collectivite-civilite"
              name="identifiantCiviliteMaire"
              label="Civilité"
              required
              options={optionsCivilites}
              value={identifiantCiviliteMaire}
              onChange={onChange}
            />
          }
          type={errors.identifiantCiviliteMaire ? 'error' : ''}
          message={errors.identifiantCiviliteMaire}
        />
        <Tooltip
          trigger={
            <Form.Input
              width={12}
              name="nomMaire"
              label="Nom du Maire"
              required
              value={_.toUpper(nomMaire)}
              onChange={onChange}
            />
          }
          type={errors.nomMaire ? 'error' : ''}
          message={errors.nomMaire}
        />
      </Form.Group>
      <Divider horizontal>ADRESSE DE LA MAIRIE</Divider>
      <TabCollectiviteAdresse
        adresse={adresse}
        onChange={onChange}
        error={errors.adresse}
      />
      <Form.Group>
        <Form.Input
          width={4}
          name="codeArrondissement"
          label="Code Arrondissement"
          value={codeArrondissement}
          style={{ paddingRight: '1em' }}
          maxLength={1}
          onChange={onChange}
        />
        <Form.Input
          width={12}
          name="libelleArrondissement"
          label="Libellé Arrondissement"
          value={libelleArrondissement}
          maxLength={50}
          onChange={onChange}
        />
      </Form.Group>
      <Form.Group>
        <Form.Field width={5} required>
          <label>Téléphone 1</label>
          <Tooltip
            trigger={
              <PhoneInput
                value={telephone1}
                data-testid="parametre-collectivite-telephone-1"
                onChange={(value) => handleChangeTelephone(value, 'telephone1')}
              />
            }
            type={errors.telephone1 ? 'error' : ''}
            message={errors.telephone1}
          />
        </Form.Field>
        <Form.Field width={5}>
          <label>Téléphone 2</label>
          <PhoneInput
            value={telephone2}
            data-testid="parametre-collectivite-telephone-2"
            onChange={(value) => handleChangeTelephone(value, 'telephone2')}
          />
        </Form.Field>
        <Form.Input
          width={6}
          name="adresseEmail"
          data-testid="parametre-collectivite-mail"
          type="email"
          label="Mail"
          icon="mail"
          value={adresseEmail}
          onChange={handleEmailChange}
        />
      </Form.Group>
      <Divider horizontal>CACHET ET SIGNATURE</Divider>
      {loadingImages ? (
        <Dimmer
          active
          inverted
          style={{ position: 'relative', height: '140px' }}
        >
          <Loader inverted>Chargement des images</Loader>
        </Dimmer>
      ) : (
        <Form.Group widths="equal">
          <Form.Field data-testid="dropzone-image-cachet">
            <label>Cachet de la Mairie (100 x 100 pixels)</label>
            {renderImage(imageCachet, 'imageCachet')}
          </Form.Field>
          <Form.Field data-testid="dropzone-image-signature">
            <label>Signature du Maire (100 x 100 pixels)</label>
            {renderImage(imageSignature, 'imageSignature')}
          </Form.Field>
        </Form.Group>
      )}
    </TabParametre>
  );
};

export default TabCollectivite;
